import Select from 'react-select';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { IPostAttachment, ILaserOrEndmill } from '@/redux/post/post.d';
import {
  SPINDLE_SPEED_MIN,
  SPINDLE_SPEED_MAX,
  SPINDLE_SPEED_DEFAULT,
} from '@/redux/post/post.constants';
import { FileNameChip } from './FileNameChip';
import { BitPicker } from './BitPicker';

export interface ToolDataDraft {
  laserOrEndmill: ILaserOrEndmill;
  bitId?: string;
  brandId?: string; // Which of the bit's brands was picked (cleared with the bit)
  spindleSpeed?: number; // Spindle speed in RPM (SPINDLE_SPEED_MIN-SPINDLE_SPEED_MAX)
}

// C2Editor: inherent branching for multi-field form editor (laser/endmill modes); not reducible
// fallow-ignore-next-line complexity
export function C2Editor({
  draft,
  onChange,
  onAdd,
  onComplete,
  canComplete,
  selectedModel,
  onRemove,
  hasExistingTools,
}: {
  draft: ToolDataDraft;
  onChange: (draft: ToolDataDraft) => void;
  onAdd: () => void;
  onComplete: () => void;
  canComplete?: boolean;
  selectedModel: IPostAttachment | null;
  onRemove: (fileHash: string) => void;
  hasExistingTools?: boolean;
}) {
  // LASER allows only one laser bit — no Add button, just Complete.
  const showAddButton = draft.laserOrEndmill !== ILaserOrEndmill.LASER;
  // Endmill: Complete only appears once a tool has been added. Laser has no
  // Add step, so Complete is the add-and-finish action and always shows.
  const showCompleteButton = !showAddButton || hasExistingTools;
  return (
    <div className="h-full flex flex-col bg-white p-4 rounded-lg shadow-sm border border-[#D6D6D6]">
      <div className="flex items-start gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium whitespace-nowrap">
              Add tool data to the
            </span>
            <div className="max-w-[140px] sm:max-w-[200px] min-w-0">
              <FileNameChip
                fileName={selectedModel?.fileName || ''}
                onRemove={() => onRemove(selectedModel?.fileHash || '')}
                size="xs"
              />
            </div>
            <span className="text-sm font-medium">Gcode</span>
          </div>
        </div>
        <button
          onClick={() => onRemove(selectedModel?.fileHash || '')}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium transition-colors"
          title="Discard gcode selection"
          type="button"
        >
          <ArrowLeft className="h-3 w-3" />
          <span>Discard</span>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-sm font-medium">Laser or Endmill</div>
            <Select
              options={[
                {
                  label: ILaserOrEndmill.ENDMILL,
                  value: ILaserOrEndmill.ENDMILL,
                },
                {
                  label: ILaserOrEndmill.LASER,
                  value: ILaserOrEndmill.LASER,
                },
              ]}
              value={{
                label: draft.laserOrEndmill,
                value: draft.laserOrEndmill,
              }}
              onChange={(opt) => {
                if (!opt) return;
                const previousType = draft.laserOrEndmill;
                const newType = opt.value;
                if (newType === previousType) return;

                // Switching kind always clears the bit selection — an Endmill
                // bitId is not a valid Laser pick and vice versa. Endmill also
                // gets a default spindle speed; Laser carries neither bitId nor
                // spindle speed until the user selects a laser.
                if (newType === ILaserOrEndmill.ENDMILL) {
                  onChange({
                    laserOrEndmill: newType,
                    bitId: undefined,
                    spindleSpeed: SPINDLE_SPEED_DEFAULT, // Default spindle speed
                  });
                } else {
                  onChange({ laserOrEndmill: newType, bitId: undefined });
                }
              }}
              isSearchable
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : undefined
              }
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {draft.laserOrEndmill === ILaserOrEndmill.ENDMILL && (
            <>
              <div className="space-y-1">
                <div className="text-sm font-medium">
                  Tool Bit <span className="text-red-500">*</span>
                </div>
                <BitPicker
                  value={draft.bitId}
                  brandValue={draft.brandId}
                  laserOrEndmill={draft.laserOrEndmill}
                  onChange={(bit, brand) =>
                    onChange({
                      ...draft,
                      bitId: bit?._id,
                      brandId: brand?._id,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Browse the catalog to pick a bit
                </p>
              </div>

              {/* Spindle Speed field - only for ENDMILL */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Spindle Speed (RPM)</div>
                <Input
                  id="spindle-speed-input"
                  type="number"
                  min={String(SPINDLE_SPEED_MIN)}
                  max={String(SPINDLE_SPEED_MAX)}
                  step="100"
                  placeholder="e.g., 18000"
                  value={draft.spindleSpeed ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    onChange({
                      ...draft,
                      spindleSpeed: value === '' ? undefined : Number(value),
                    });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Enter spindle speed in RPM ({SPINDLE_SPEED_MIN}-
                  {SPINDLE_SPEED_MAX})
                </p>
              </div>
            </>
          )}

          {draft.laserOrEndmill === ILaserOrEndmill.LASER && (
            <div className="space-y-1">
              <div className="text-sm font-medium">
                Laser <span className="text-red-500">*</span>
              </div>
              <BitPicker
                value={draft.bitId}
                brandValue={draft.brandId}
                laserOrEndmill={draft.laserOrEndmill}
                placeholder="Select a laser from the catalog"
                onChange={(bit, brand) =>
                  onChange({ ...draft, bitId: bit?._id, brandId: brand?._id })
                }
              />
              <p className="text-xs text-muted-foreground">
                Select which laser to use
              </p>
            </div>
          )}
        </div>
      </div>

      <div
        className={`pt-3 ${
          showAddButton && showCompleteButton ? 'grid grid-cols-2 gap-2' : ''
        }`}
      >
        {showAddButton && (
          <Button
            variant="secondary"
            onClick={onAdd}
            className={showCompleteButton ? '' : 'w-full'}
          >
            {draft.laserOrEndmill === ILaserOrEndmill.ENDMILL &&
            hasExistingTools
              ? 'Add another tool to this toolpath gcode'
              : 'Add'}
          </Button>
        )}
        {showCompleteButton && (
          <Button
            onClick={onComplete}
            disabled={canComplete === false}
            className={showAddButton ? '' : 'w-full'}
          >
            Complete
          </Button>
        )}
      </div>
    </div>
  );
}

export default C2Editor;
